import type { ColumnType } from "@savana/ant-design-vue/es/table";
import {
    computed,
    defineComponent,
    h,
    inject,
    PropType,
    provide,
    VNodeTypes,
} from "vue";
import { EllipsisOutlined } from "@ant-design/icons-vue";
import { Dropdown, Menu, Popconfirm } from "@savana/ant-design-vue";
import IButton from "@/components/i-components/IButton/IButton";
import "./ActionCell.less";
import { devConsole } from "@savana/core";
import {
    ActionCheckTypes,
    OperationAction,
    OperationActionKey,
} from "@savana/business-core/solutions/declare";

const MenuItem = Menu.Item;

export const generateRowActionCell = <RecordType,>(
    actions: OperationAction<RecordType>[],
    checkActionFn: ActionCheckTypes,
    onClick: (
        actionKey: string,
        record: any | undefined,
        editableRecordContext:
            | {
                  validate: () => Promise<boolean>;
              }
            | undefined
    ) => void,
    disabled: (
        actionKey: OperationActionKey,
        record: any | undefined
    ) => boolean,
    visible: (
        actionKey: OperationActionKey,
        record: any | undefined
    ) => boolean,
    maxShowLength: number = 3
) => {
    return (opt: {
        value: any;
        text: any;
        record: RecordType;
        index: number;
        column: ColumnType<RecordType>;
    }) => {
        return (
            <ActionCell
                record={opt.record}
                actions={actions.filter(a => visible(a.key, opt.record))}
                checkActionFn={checkActionFn}
                maxShowLength={maxShowLength}
                onClick={onClick}
                disableFn={disabled}
            />
        );
    };
};

export const ActionCell = defineComponent({
    components: {
        EllipsisOutlined,
    },
    emits: ["click"],
    props: {
        actions: {
            type: Array as PropType<OperationAction[]>,
            default: () => [],
        },
        checkActionFn: {
            type: Function as PropType<ActionCheckTypes>,
            default: () => true,
        },
        record: {
            type: Object as PropType<any>,
            required: true,
        },
        maxShowLength: {
            type: Number,
            default: 3,
        },
        disableFn: {
            type: Function as PropType<
                (actionKey: string, currentRecord: any | undefined) => boolean
            >,
            default: () => false,
        },
        showSeprator: {
            type: Boolean,
            default: false,
        },
    },
    setup(props, context) {
        const validActions = computed(() =>
            props.actions.filter(a => props.checkActionFn(a.actionType))
        );
        const showActions = computed(() => {
            let length = props.actions.length;
            if (props.actions.length > props.maxShowLength) {
                length = props.maxShowLength;
            }
            return validActions.value.slice(0, length);
        });
        const expandActions = computed(() => {
            if (props.actions.length > props.maxShowLength) {
                return validActions.value.slice(props.maxShowLength);
            }
            return [];
        });

        const checkSepratorShow = (index: number) => {
            if (props.showSeprator) {
                if (index === showActions.value.length - 1) {
                    return expandActions.value.length > 0;
                }
                return index < showActions.value.length - 1;
            } else {
                return false;
            }
        };

        provide("disableFn", (actionKey: string) =>
            props.disableFn(actionKey, props.record)
        );

        const editableRecordContext = inject("editableRecord", null);

        return () => {
            if (props.actions.length > 0) {
                return (
                    <div class="operation-actions">
                        {showActions.value.map((action, index) => {
                            const onClick = () => {
                                context.emit(
                                    "click",
                                    action.key,
                                    props.record,
                                    editableRecordContext
                                );
                            };
                            const actionClick = (key: string) => {
                                context.emit(
                                    "click",
                                    key,
                                    props.record,
                                    editableRecordContext
                                );
                            };
                            if (action.customRender) {
                                return [
                                    h(action.customRender, {
                                        record: props.record,
                                        actionClick,
                                    }),
                                    <div
                                        key="seprator"
                                        v-show={checkSepratorShow(index)}
                                        class="seprator"
                                    ></div>,
                                ];
                            }
                            let title: any = action.title as string;
                            if (typeof action.title !== "string") {
                                title = () => {
                                    return (
                                        action.title as (
                                            record: any
                                        ) => VNodeTypes
                                    )(props.record);
                                };
                            }
                            return (
                                <OperationActionButton
                                    popConfirm={action.popConfirm}
                                    actionKey={action.key}
                                    key={action.key.toString()}
                                    record={props.record}
                                    title={title}
                                    onClick={onClick}
                                    showSeprator={checkSepratorShow(index)}
                                />
                            );
                        })}
                        <OperationActionDropdown
                            onClick={(actionKey, record) => {
                                context.emit("click", actionKey, record);
                            }}
                            items={expandActions.value}
                            record={props.record}
                        />
                    </div>
                );
            }
            return null;
        };
    },
});

export const OperationActionButton = defineComponent({
    props: {
        actionKey: {
            type: [String, Symbol],
            required: true,
        },
        title: {
            type: [String, Function] as PropType<
                string | ((record: any) => VNodeTypes)
            >,
            default: () => "",
        },
        popConfirm: {
            type: Object as PropType<OperationAction["popConfirm"]>,
        },
        showSeprator: {
            type: Boolean,
            default: true,
        },
        record: Object,
    },
    components: {
        IButton,
        Popconfirm,
    },
    emits: ["click"],
    setup(props, context) {
        const onClick = (e: Event) => {
            e.stopPropagation();
        };

        const onConfirm = (e: Event) => {
            e.stopPropagation();
            if (disabled.value) {
                devConsole("action disabled!");
                return;
            }
            context.emit("click");
        };

        const disabled = useDisable(props.actionKey);
        const content = computed(() =>
            typeof props.title === "string" ? () => props.title : props.title
        );

        if (props.popConfirm) {
            const { popConfirmProps, popConfirmTitle } = usePopconfirm(
                props,
                props
            );
            const defaultRender = () => (
                <IButton
                    class="action-button"
                    size="small"
                    type="link"
                    disabled={disabled.value}
                    onClick={onClick}
                >
                    {{ default: content.value }}
                </IButton>
            );
            return () => (
                <>
                    <Popconfirm
                        {...popConfirmProps.value}
                        onConfirm={onConfirm}
                    >
                        {{
                            title: popConfirmTitle,
                            default: defaultRender,
                        }}
                    </Popconfirm>
                    <div v-show={props.showSeprator} class="seprator"></div>
                </>
            );
        }
        return () => (
            <>
                <IButton
                    class="action-button"
                    size="small"
                    type="link"
                    disabled={disabled.value}
                    onClick={onConfirm}
                >
                    {{ default: content.value }}
                </IButton>
                <div v-show={props.showSeprator} class="seprator"></div>
            </>
        );
    },
});

export const OperationActionDropdown = defineComponent({
    emits: ["click"],
    props: {
        items: {
            type: Array as PropType<Array<OperationAction>>,
            default: () => [],
        },
        context: {
            type: Object,
            default: () => {},
        },
        record: {
            type: Object as PropType<any>,
            default: () => {},
        },
    },
    components: {
        EllipsisOutlined,
        Dropdown,
        Menu,
        MenuItem,
    },
    setup(props, context) {
        if (props.items.length < 1) {
            return () => null;
        }
        const onClick = (e: Event) => {
            e.stopPropagation();
        };

        const slots = {
            default: () => <EllipsisOutlined onClick={onClick} />,
            overlay: () => (
                <Menu>
                    {props.items.map(item => {
                        const disabled = useDisable(item.key);
                        if (item.popConfirm) {
                            const { popConfirmProps, popConfirmTitle } =
                                usePopconfirm(item as any, props);
                            const onConfirm = (e: Event) => {
                                e.stopPropagation();
                                if (disabled.value) {
                                    devConsole("action disabled!");
                                    return;
                                }
                                context.emit("click", item.key, props.record);
                            };
                            return (
                                <MenuItem
                                    key={item.key.toString()}
                                    disabled={disabled.value}
                                    onClick={onClick}
                                >
                                    <Popconfirm
                                        {...popConfirmProps.value}
                                        onConfirm={onConfirm}
                                    >
                                        {{
                                            title: popConfirmTitle,
                                            default: () =>
                                                typeof item.title === "string"
                                                    ? item.title
                                                    : item.title(props.record),
                                        }}
                                    </Popconfirm>
                                </MenuItem>
                            );
                        } else {
                            return (
                                <MenuItem
                                    key={item.key}
                                    disabled={disabled.value}
                                    onClick={() => {
                                        context.emit(
                                            "click",
                                            item.key,
                                            props.record
                                        );
                                    }}
                                >
                                    {typeof item.title === "string"
                                        ? item.title
                                        : item.title(props.record)}
                                </MenuItem>
                            );
                        }
                    })}
                </Menu>
            ),
        };
        return () => (
            <Dropdown overlayClassName="action-dropdown">{slots}</Dropdown>
        );
    },
});

const useDisable = (actionKey: OperationActionKey) => {
    const disableFn = inject<(actionKey: OperationActionKey) => boolean>(
        "disableFn",
        () => false
    );

    return computed(() => disableFn(actionKey));
};

const usePopconfirm = (
    props: {
        title: string | ((record: any) => VNodeTypes);
        popConfirm: OperationAction["popConfirm"];
    },
    record: { record?: Object }
) => {
    const popConfirmProps = computed(() => {
        return {
            ...props.popConfirm,
            title: undefined,
        };
    });
    const popConfirmTitle = () => {
        if (typeof props.title === "string") {
            return props.title;
        }
        return props.title(record.record);
    };
    return {
        popConfirmProps,
        popConfirmTitle,
    };
};
